import { Link, useLocation } from 'react-router-dom';
import { HomeIcon, BookOpenIcon, BeakerIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useState, useRef } from 'react';
import MedicinalPropertiesSidebar from './MedicinalPropertiesSidebar';
import { useAdmin } from '../context/AdminContext';

const TAP_RESET_MS = 2000;
const TAPS_NEEDED = 3;

const Navbar = () => {
  const location = useLocation();
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const tapCountRef = useRef(0);
  const tapTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { setShowPinModal } = useAdmin();

  const handleLogoTap = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
      tapTimeoutRef.current = null;
    }
    tapCountRef.current += 1;
    if (tapCountRef.current >= TAPS_NEEDED) {
      tapCountRef.current = 0;
      setShowPinModal(true);
    } else {
      tapTimeoutRef.current = setTimeout(() => {
        tapCountRef.current = 0;
        tapTimeoutRef.current = null;
      }, TAP_RESET_MS);
    }
  };
  
  useEffect(() => {
    // Check if the app is loaded in embedded mode via URL parameter
    const params = new URLSearchParams(window.location.search);
    setIsEmbedded(params.get('embedded') === 'true');
  }, []);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);
  
  const navItems = [
    { to: '/', icon: HomeIcon, label: 'Home', external: false, hideWhenEmbedded: false },
    { to: '/botanicals', icon: BookOpenIcon, label: 'Browse Botanicals', external: false, hideWhenEmbedded: false },
  ];
  
  // Filter out items that should be hidden when embedded
  const visibleNavItems = navItems.filter(item => !(isEmbedded && item.hideWhenEmbedded));

  const NavContent = () => (
    <>
      <div className="flex items-center space-x-3 mb-12 p-3 rounded-lg bg-white/10 backdrop-blur-sm">
        <button
          type="button"
          onClick={handleLogoTap}
          className="p-2 bg-white/20 rounded-lg cursor-pointer touch-manipulation select-none hover:bg-white/25 transition-colors"
          aria-label="Logo"
        >
          <BeakerIcon className="h-6 w-6 text-white" />
        </button>
        <div>
          <h1 className="text-xl font-bold">Materia Medica</h1>
          <p className="text-xs text-primary-100 opacity-80">NURTURED BY NATURE</p>
        </div>
      </div>
      
      <nav className="space-y-1">
        {visibleNavItems.map((item) => {
          const isActive = !item.external && location.pathname === item.to;
          const linkClass = `flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 ${
            isActive 
              ? 'bg-white/10 text-white font-medium' 
              : 'text-white/80 hover:bg-white/5 hover:text-white'
          }`;
          
          return item.external ? (
            <a
              key={item.to}
              href={item.to}
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass}
            >
              {item.icon && <item.icon className="h-5 w-5" />}
              <span>{item.label}</span>
            </a>
          ) : (
            <Link
              key={item.to}
              to={item.to}
              className={linkClass}
            >
              {item.icon && <item.icon className="h-5 w-5" />}
              <span>{item.label}</span>
            </Link>
          );
        })}
      </nav>
      
      {/* Medicinal Properties Filter */}
      <MedicinalPropertiesSidebar />

      {!isEmbedded && (
        <div className="absolute bottom-6 left-6 right-6">
          <p className="text-xs text-white/50 text-center">
            © {new Date().getFullYear()} MYECO EARTH
          </p>
        </div>
      )}
    </>
  );

  return (
    <>
      {/* Mobile Header Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#112437] text-white px-4 py-3 flex items-center justify-between shadow-lg">
        <div className="flex items-center space-x-3">
          <div className="p-1.5 bg-white/20 rounded-lg">
            <BeakerIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-lg font-bold">Materia Medica</h1>
          </div>
        </div>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? (
            <XMarkIcon className="h-6 w-6" />
          ) : (
            <Bars3Icon className="h-6 w-6" />
          )}
        </button>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`lg:hidden fixed inset-0 bg-black/50 z-40 transition-opacity duration-300
          ${isMobileMenuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}
        `}
        onClick={() => setIsMobileMenuOpen(false)}
      />

      {/* Mobile Slide-out Menu */}
      <div className={`
        lg:hidden fixed top-14 left-0 h-[calc(100%-3.5rem)] w-72 bg-[#112437] text-white p-6 shadow-xl z-[60]
        transform transition-transform duration-300 ease-in-out overflow-y-auto
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <nav className="space-y-1 mb-6">
          {visibleNavItems.map((item) => {
            const isActive = !item.external && location.pathname === item.to;
            const linkClass = `flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 ${
              isActive 
                ? 'bg-white/10 text-white font-medium' 
                : 'text-white/80 hover:bg-white/5 hover:text-white'
            }`;
            
            return (
              <Link
                key={item.to}
                to={item.to}
                className={linkClass}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {item.icon && <item.icon className="h-5 w-5" />}
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>
        <MedicinalPropertiesSidebar />
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:block fixed left-0 top-0 h-full w-64 bg-[#112437] text-white p-6 shadow-xl">
        <NavContent />
      </div>
    </>
  );
};

export default Navbar;
